/* @ngInject */

export default (
  $q,
  $rootScope,
  helperService,
  helperPdfService,
  assetFinanceService,
  quickToolService,
  dateTimeService,
  INSTANCE
) => ({
  createAssetFinanceCompareProducts: ({
    assetDetails,
    compareProducts,
    lenders,
    documents,
    ticket,
    isCommercial,
    isQuickTool,
    isView
  }) => {
    const deferred = $q.defer();
    const currentOrganization = $rootScope.CurrentOrganization;
    const currentAccount = $rootScope.CurrentAccount;

    const getLenderDocument = ({ idLender }) => {
      const doc = _.find(
        documents || [],
        (d) => d.lender[0]?.id === idLender && d?.type?.indexOf('LOGO') > -1
      );
      return doc?.url || '';
    };

    const getLenderLogo = ({ lenderLogos, idLender }) => {
      const logoBase64 = _.find(lenderLogos, (l) => l?.id === idLender);
      return (
        logoBase64?.logo ||
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8Xw8AAoMBgDTD2qgAAAAASUVORK5CYII='
      );
    };

    const getLenderNameOrLogo = ({ lenderLogos, idLender, isLogo }) => {
      if (isLogo) {
        return getLenderLogo({ lenderLogos, idLender });
      }
      const lender = _.find(lenders, (l) => l?.id === idLender);
      return lender?.name || 'N/A';
    };

    const getTotalAssetNetCost = () =>
      _.reduce(assetDetails || [], (sum, n) => _.add(sum, _.toNumber(n?.netCost || 0)), 0);

    const rows = [
      'Lender',
      'Product type',
      'Net amount financed',
      'Net cost of asset',
      'Loan term',
      'Balloon percent',
      'Balloon value',
      'Customer rate',
      'Monthly repayments excl monthly fees',
      'Payments',
      'Fees',
      'Upfront fee',
      'Total broker origination fee',
      'Monthly fee',
      'Estimated cost',
      'Total principal',
      'Total interest',
      'Total fees',
      'Total cost'
    ];

    const getProductSplitRow = () => ({
      colSpan: 2,
      text: '',
      style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
    });

    const currentCurrency = $rootScope.CurrentCurrency();
    const lenderLogos = [];
    const compareDataTableWidth = ['*'];
    const compareDataRows = [];
    const queue = [];
    compareProducts.forEach((product) => {
      queue.push(
        $q.when(
          quickToolService
            .getBase64FromImageUrl({
              url: getLenderDocument({
                idLender: product?.selectedProduct?.lender[0]?.id
              })
            })
            .then((res) => {
              lenderLogos.push({
                id: product?.selectedProduct?.lender[0]?.id,
                logo: res.data
              });
            })
        )
      );
    });
    return $q.all(queue).then(() => {
      const rowsLen = rows.length;
      for (let i = 0; i < rowsLen; i += 1) {
        const row = rows[i];
        const compareDataRow = [];
        compareDataRow.push({
          text: row,
          margin: i === 0 ? [4, 4, 0, 0] : [4, 2, 0, 2],
          fillColor: i === 10 || i === 14 ? '#eee' : '#fff',
          bold: i === 10 || i === 14,
          style: ['tableFontSize', 'tableCellLeftAlign']
        });
        for (let j = 0; j < compareProducts?.length; j += 1) {
          const compareProduct = compareProducts[j];
          if (row === 'Lender') {
            compareDataTableWidth.push(20, '*');
            compareDataRow.push(
              {
                border: [true, true, false, true],
                width: 18,
                height: 18,
                margin: [1, 1, 1, 1],
                image: getLenderNameOrLogo({
                  lenderLogos,
                  idLender: compareProduct?.selectedProduct?.lender[0]?.id,
                  isLogo: true
                }),
                style: ['tableCellCenterAlign']
              },
              {
                border: [false, true, true, true],
                text: getLenderNameOrLogo({
                  lenderLogos,
                  idLender: compareProduct?.selectedProduct?.lender[0]?.id,
                  isLogo: false
                }),
                margin: [2, 4, 0, 0],
                style: ['tableFontSize', 'tableCellLeftAlign']
              }
            );
          }
          if (row === 'Product type') {
            const item = getProductSplitRow();
            const productType = assetFinanceService.getProductType({
              productType: compareProduct?.productDetails?.productType,
              isCommercial,
              isQuickTool
            });
            item.text = productType?.value || 'N/A';
            compareDataRow.push(item, '');
          }
          if (row === 'Net amount financed') {
            const item = getProductSplitRow();
            item.text = compareProduct?.productDetails?.netAmountFinanced?.formatWithCurrency(
              currentCurrency,
              2
            );
            compareDataRow.push(item, '');
          }
          if (row === 'Net cost of asset') {
            const item = getProductSplitRow();
            item.text = getTotalAssetNetCost()?.formatWithCurrency(currentCurrency, 2);
            compareDataRow.push(item, '');
          }
          if (row === 'Loan term') {
            const item = getProductSplitRow();
            const term = assetFinanceService.getLoanTerm({
              term: compareProduct?.productDetails?.term
            });
            item.text = term?.title || '-';
            compareDataRow.push(item, '');
          }
          if (row === 'Balloon percent') {
            const item = getProductSplitRow();
            item.text = `${compareProduct?.productDetails?.balloonPercent || 0}%`;
            compareDataRow.push(item, '');
          }
          if (row === 'Balloon value') {
            const item = getProductSplitRow();
            item.text = compareProduct?.productDetails?.balloonValue?.formatWithCurrency(
              currentCurrency,
              2
            );
            compareDataRow.push(item, '');
          }
          if (row === 'Customer rate') {
            const item = getProductSplitRow();
            item.text = `${compareProduct?.productDetails?.estimatedBorrowerRate || 0}%`;
            compareDataRow.push(item, '');
          }
          if (row === 'Monthly repayments excl monthly fees') {
            const item = getProductSplitRow();
            item.text = compareProduct?.repayments.repayment?.formatWithCurrency(
              currentCurrency,
              2
            );
            compareDataRow.push(item, '');
          }
          if (row === 'Payments') {
            const item = getProductSplitRow();
            item.text = compareProduct?.productDetails?.payments ? 'Advance' : 'Arrears';
            compareDataRow.push(item, '');
          }
          if (row === 'Fees') {
            const item = getProductSplitRow();
            item.bold = true;
            item.fillColor = '#eeeeee';
            compareDataRow.push(item, '');
          }
          if (row === 'Upfront fee') {
            const item = getProductSplitRow();
            item.text = [
              {
                text: compareProduct?.productDetails.upfrontFee?.formatWithCurrency(
                  currentCurrency,
                  2
                )
              },
              {
                text: ` ${
                  compareProduct?.productDetails.isUpfrontFeeCapitalised ? '(Capitalised)' : ''
                }\n`,
                fontSize: 9
              }
            ];
            compareDataRow.push(item, '');
          }
          if (row === 'Total broker origination fee') {
            const item = getProductSplitRow();
            item.text = [
              {
                text: compareProduct?.productDetails.brokerOriginationFee?.formatWithCurrency(
                  currentCurrency,
                  2
                )
              },
              {
                text: ` ${
                  compareProduct?.productDetails.isBrokerOriginationFeeCapitalized
                    ? '(Capitalised)'
                    : ''
                }\n`,
                fontSize: 9
              }
            ];
            compareDataRow.push(item, '');
          }
          if (row === 'Monthly fee') {
            const item = getProductSplitRow();
            item.text = compareProduct?.repayments?.monthlyFee?.formatWithCurrency(
              currentCurrency,
              2
            );
            compareDataRow.push(item, '');
          }
          if (row === 'Estimated cost') {
            const item = getProductSplitRow();
            item.bold = true;
            item.fillColor = '#eeeeee';
            compareDataRow.push(item, '');
          }
          if (row === 'Total principal') {
            const item = getProductSplitRow();
            item.text = compareProduct?.costs?.principal?.formatWithCurrency(
              currentCurrency,
              2
            );
            compareDataRow.push(item, '');
          }
          if (row === 'Total interest') {
            const item = getProductSplitRow();
            item.text = compareProduct?.costs.interest?.formatWithCurrency(currentCurrency, 2);
            compareDataRow.push(item, '');
          }
          if (row === 'Total fees') {
            const item = getProductSplitRow();
            item.text = compareProduct?.costs.fees?.formatWithCurrency(currentCurrency, 2);
            compareDataRow.push(item, '');
          }
          if (row === 'Total cost') {
            const item = getProductSplitRow();
            item.text = compareProduct?.costs.total?.formatWithCurrency(currentCurrency, 2);
            compareDataRow.push(item, '');
          }
        }
        const compareRow = _.flatten(compareDataRow);
        compareDataRows.push(compareRow);
      }

      const now = moment.utc().tz($rootScope.CurrentTimeZone()).format('X');
      const date = dateTimeService.getFormattedDate(now);
      const orgLogo = currentOrganization?.organization?.logo
        ? currentOrganization.organization.logo.replace(
            /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi,
            currentOrganization.id
          )
        : INSTANCE.url + INSTANCE.logo;
      quickToolService.getBase64FromImageUrl({ url: orgLogo }).then((res) => {
        const docDefinition = {
          pageSize: 'A4',
          pageOrientation: 'landscape',
          pageMargins: [40, 20, 40, 20],
          content: [
            {
              table: {
                widths: [240, '*'],
                headerRows: 1,
                body: [
                  [
                    {
                      width: 180,
                      height: 30,
                      image: res.data,
                      margin: [5, 3, 0, 3],
                      style: 'tableHeader'
                    },
                    {
                      fillColor: '#eeeeee',
                      text: `Prepared by: ${currentAccount.getName()}\nPrepared on: ${date}`,
                      style: 'prepared'
                    }
                  ]
                ]
              },
              layout: 'noBorders'
            },
            {
              text: 'Product comparison',
              style: ['header', 'marginTop']
            },
            {
              table: {
                dontBreakRows: true,
                headerRows: 1,
                widths: compareDataTableWidth,
                body: compareDataRows
              },
              style: ['marginTop'],
              layout: 'defaultLayout'
            }
          ],
          styles: {
            header: {
              fontSize: 16,
              bold: true
            },
            subHeader: {
              fontSize: 14,
              bold: true,
              margin: [0, 10, 0, 0]
            },
            prepared: {
              fontSize: 10,
              alignment: 'right',
              margin: [0, 6, 5, 0]
            },
            marginTop: {
              margin: [0, 10, 0, 0]
            },
            tableFontSize: {
              fontSize: 10
            },
            tableMarginTop: {
              margin: [0, 5, 0, 0]
            },
            tableHeader: {
              fillColor: '#eee'
            },
            tableCellMarginTop: {
              margin: [0, 2, 0, 0]
            },
            tableCellMarginLeftTopBottom: {
              margin: [4, 2, 0, 2]
            },
            tableCellMarginRightTopBottom: {
              margin: [0, 2, 4, 2]
            },
            tableCellMarginTopBottom: {
              margin: [0, 2, 0, 2]
            },
            tableCellLeftAlign: {
              alignment: 'left'
            },
            tableCellRightAlign: {
              alignment: 'right'
            },
            tableCellCenterAlign: {
              alignment: 'center'
            }
          }
        };
        pdfMake.tableLayouts = {
          defaultLayout: {
            hLineWidth(i, node) {
              return i === node.table.headerRows ? 1 : 0.5;
            },
            vLineWidth() {
              return 0.5;
            },
            hLineColor(i, node) {
              return i === 0 || i === node.table.body.length ? '#333' : '#aaa';
            },
            vLineColor(i, node) {
              return i === 0 || i === node.table.widths.length ? '#333' : '#aaa';
            },
            paddingLeft() {
              return 0;
            },
            paddingRight() {
              return 0;
            },
            paddingTop() {
              return 0;
            },
            paddingBottom() {
              return 0;
            }
          }
        };
        const fileName = `${moment.utc().year()}${moment.utc().month() + 1}${moment
          .utc()
          .date()}${isQuickTool ? ' Quick tools -' : ''} Product comparison.pdf`;
        if (isQuickTool && !_.isEmpty(docDefinition)) {
          if (!_.isEmpty(docDefinition)) {
            if (isView) {
              pdfMake.createPdf(docDefinition).open();
            } else {
              pdfMake.createPdf(docDefinition).download(fileName);
            }
            deferred.resolve();
          }
        } else if (!isQuickTool && !_.isEmpty(docDefinition)) {
          if (isView) {
            helperPdfService.viewOrSavePdf({ docDefinition, isView: true }).then(() => {
              deferred.resolve();
            });
          } else {
            helperPdfService
              .viewOrSavePdf({
                ticket,
                docDefinition,
                fileName,
                isView: false
              })
              .then(() => {
                deferred.resolve();
              });
          }
        }
      });
      return deferred.promise;
    });
  }
});
